import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../UI/base/Button';
import { Modal, InputField, InputFieldNumber } from '../UI';
import { getDkkSymbol, formatDkk } from '../../utils/currencyUtils';
import { REIMBURSEMENT_NOTE_MAX_LENGTH } from '../../constants/economicsConstants';
import type { EconomicsGroupNode } from '../../types/economicsTypes';
import styles from './ReimbursementOverrideModal.module.css';

interface ReimbursementOverrideModalProps {
  node: EconomicsGroupNode;
  onClose: () => void;
  onConfirm: (value: number | null, note: string) => void;
}

/**
 * Editing what a completed group earns goes through this dialog rather than an inline field:
 * the amount is reported to the government, so it must be a deliberate act carrying a reason.
 */
export const ReimbursementOverrideModal = ({
  node,
  onClose,
  onConfirm
}: ReimbursementOverrideModalProps) => {
  const { t, i18n } = useTranslation();

  const [amount, setAmount] = useState<number>(node.reimbursementOverride ?? node.defaultAmount);
  const [note, setNote] = useState<string>(node.note ?? '');
  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);

  // Typing the group's own amount means "no override" — the leaf falls back to the default.
  const nextOverride = amount === node.defaultAmount ? null : amount;
  const isAmountChanged = nextOverride !== node.reimbursementOverride;
  const isNoteMissing = note.trim().length === 0;
  const isNoteTooLong = note.trim().length > REIMBURSEMENT_NOTE_MAX_LENGTH;

  // Only a change to the amount needs explaining; reopening the dialog and closing it unchanged
  // is not a change at all, so it must not be blocked behind a note.
  const canConfirm = !isAmountChanged || (!isNoteMissing && !isNoteTooLong);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = event.target.value;
    const parsed = raw === '' ? node.defaultAmount : Number(raw);
    if (Number.isNaN(parsed)) return;
    setAmount(parsed);
  };

  const handleConfirm = (): void => {
    setHasTriedToSubmit(true);
    if (!canConfirm) return;
    onConfirm(nextOverride, note.trim());
  };

  let noteError: string | undefined;
  if (hasTriedToSubmit && isAmountChanged && isNoteMissing) {
    noteError = t('economics-note-required');
  } else if (isNoteTooLong) {
    noteError = t('economics-note-too-long', { max: REIMBURSEMENT_NOTE_MAX_LENGTH });
  }

  return (
    <Modal
      title={t('economics-override-title')}
      onClose={onClose}
      width={568}
      contentStyle={{ padding: '0 var(--padding-2xl)' }}
      footer={
        <div className={styles.footer}>
          <Button type="secondary" onClick={onClose}>
            {t('economics-override-cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm} disabled={!canConfirm}>
            {t('economics-override-confirm')}
          </Button>
        </div>
      }
    >
      <p className={styles.context}>
        {t('economics-override-context', {
          group: node.groupName,
          week: node.weekNumber,
          amount: formatDkk(node.defaultAmount, i18n.language)
        })}
      </p>

      <InputFieldNumber
        label={t('economics-override-amount')}
        value={amount}
        onChange={handleAmountChange}
        suffixLabel={getDkkSymbol(i18n.language)}
        allowNegative={false}
        textAlign="right"
        width="180px"
      />

      <InputField
        label={t('economics-override-note')}
        required
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder={t('economics-note-placeholder')}
        error={noteError}
      />

      <p className={styles.hint}>{t('economics-note-hint')}</p>
    </Modal>
  );
};
